/*
 * tabActivityTracking.ts
 *
 * Per-tab engagement telemetry for the logged-in analytics dashboard. Captures
 * post-load active time-on-site (`active_ms_after_ready`) — active dwell measured
 * AFTER a tab's content has finished loading, so a tab's load/render time cannot
 * inflate the number — plus per-tab load time (`time_to_ready_ms`). Originally
 * built to power the analytics_dashboard_tos_v2_2026_q3 A/B test; kept as
 * permanent instrumentation after that test closed.
 *
 * DELIBERATELY ISOLATED from the sitewide page-activity tracker
 * (core_webpack/metrics/page_activity_tracking.ts): this module owns its own
 * visible+focused+idle timer and emits to the `arbitrary_super_events` Redshift
 * table via the generic arbitrary-events endpoint, so per-tab measurement neither
 * changes nor depends on the sitewide time-on-site metric.
 *
 * Model: one tab visit == one hit (loadRecordHit re-keys window.Aedu.hit_data on
 * each SPA tab change). We emit an AnalyticsTabActivity event per tab-hit, keyed
 * by that hit_id — attached by the emit helper from window.Aedu.hit_data.id, read
 * at emit time, which is always this tab's hit (route_change emits before the
 * re-key; hidden/pagehide emit while the tab is current). Events are CUMULATIVE
 * snapshots (a tab may emit on hide and again on leave/unload), so downstream
 * analysis aggregates by hit_id taking the MAX per numeric field.
 *
 * Wiring:
 *   - startTab()        — AnalyticsContainer mount (landing) + synchronously on each
 *                         AnalyticsTabRoute nav (BEFORE the async loadRecordHit), so a
 *                         fast/cached tab's load still registers.
 *   - observeLoading()  — AnalyticsContainer, driven by the Redux store (any fetch in flight)
 *   - endTab('route_change') — AnalyticsTabRoute, before loadRecordHit re-keys the hit
 *   - hidden / pagehide — handled internally
 */
import { write } from 'js/core_webpack/arbitrary_events';

const IDLE_TIMEOUT_MS = 60_000; // matches the sitewide tracker's idle window
const CACHED_READY_DEFER_MS = 400; // grace before treating a no-fetch tab as ready
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

type EndReason = 'route_change' | 'pagehide' | 'hidden';

// --- current tab-hit state ---
let tab = '';
let tabIndex = -1; // landing becomes 0
let tabStartWall = 0;

// --- active-time accumulator (visible + focused + non-idle) ---
let activeMs = 0;
let runningSince: number | null = null;
let idle = false;
let idleTimer: ReturnType<typeof setTimeout> | undefined;

// --- content-ready state ---
let readyMarked = false;
let sawLoading = false;
let readyAtActiveMs: number | null = null;
let timeToReadyMs: number | null = null;
let cachedReadyTimer: ReturnType<typeof setTimeout> | undefined;

let listenersBound = false;

function currentHitId(): number | null {
  return (
    (window.Aedu && window.Aedu.hit_data && window.Aedu.hit_data.id) || null
  );
}

function shouldRun(): boolean {
  return (
    !idle &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'visible' &&
    document.hasFocus()
  );
}

function resume(): void {
  if (runningSince === null && shouldRun()) {
    runningSince = Date.now();
  }
}

function pause(): void {
  if (runningSince !== null) {
    activeMs += Date.now() - runningSince;
    runningSince = null;
  }
}

function activeMsNow(): number {
  return Math.round(
    activeMs + (runningSince !== null ? Date.now() - runningSince : 0),
  );
}

function onActivity(): void {
  if (idle) {
    idle = false;
  }
  resume();
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idle = true;
    pause();
  }, IDLE_TIMEOUT_MS);
}

function tabFromLocation(): string {
  if (typeof window === 'undefined') return '';
  const m = window.location.pathname.match(/\/Analytics\/activity\/([^/?#]+)/i);
  return m ? decodeURIComponent(m[1]) : '';
}

function emit(reason: EndReason): void {
  // hit_id is attached by write() from window.Aedu.hit_data.id (read at emit time),
  // which is still this tab's hit at every call site (route_change emits before the
  // re-key). Skip if no hit is established yet.
  if (!currentHitId()) return;
  const total = activeMsNow();
  // Best-effort telemetry: a failed POST must never surface as an unhandled
  // rejection or affect the dashboard.
  try {
    const sent = write('AnalyticsTabActivity', {
      tab: tab || tabFromLocation() || 'unknown',
      tab_index: tabIndex >= 0 ? tabIndex : null,
      time_to_ready_ms: timeToReadyMs,
      active_ms_total: total,
      active_ms_after_ready:
        readyAtActiveMs === null ? null : Math.max(0, total - readyAtActiveMs),
      end_reason: reason,
    });
    if (sent && typeof sent.catch === 'function') sent.catch(() => {});
  } catch (_) {
    // swallow
  }
}

function bindListenersOnce(): void {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;

  ACTIVITY_EVENTS.forEach((e) =>
    window.addEventListener(e, onActivity, { passive: true }),
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onActivity(); // clears idle, resumes, restarts idle timer
    } else {
      pause();
      emit('hidden');
    }
  });

  window.addEventListener('pagehide', () => {
    pause();
    emit('pagehide');
  });
}

// Arm the "already loaded, no fetch" grace timer: if nothing reports a fetch
// in flight within the grace window, treat the tab as ready. A fetch starting
// (observeLoading(true)) cancels it, so we instead mark ready on fetch completion.
function armCachedReadyDefer(): void {
  if (readyMarked || sawLoading || cachedReadyTimer) return;
  cachedReadyTimer = setTimeout(() => {
    cachedReadyTimer = undefined;
    if (!readyMarked && !sawLoading) markReady();
  }, CACHED_READY_DEFER_MS);
}

/**
 * Begin tracking a new tab-hit. Called at dashboard mount (landing tab) and
 * synchronously on each SPA tab change — BEFORE the async loadRecordHit re-keys
 * window.Aedu.hit_data — so the clock and ready-detection start at tab entry and
 * a fast/cached tab's load still registers. The hit_id is read from the global at
 * emit time, so it need not be known here.
 */
export function startTab(): void {
  bindListenersOnce();
  pause(); // defensively close any prior running segment

  tab = tabFromLocation();
  tabIndex += 1;
  tabStartWall = Date.now();

  activeMs = 0;
  runningSince = null;
  idle = false;

  readyMarked = false;
  sawLoading = false;
  readyAtActiveMs = null;
  timeToReadyMs = null;
  if (cachedReadyTimer) {
    clearTimeout(cachedReadyTimer);
    cachedReadyTimer = undefined;
  }

  onActivity(); // start the clock (if visible+focused) and arm the idle timer
  armCachedReadyDefer(); // in case this tab renders already-loaded (no fetch)
}

/**
 * Report whether the dashboard's data is currently loading (any AnalyticsDashboard
 * fetch in flight). Content-ready (and thus the start of post-load active time) is
 * the first true->false transition of `isLoading`; a tab that renders
 * already-loaded (no fetch) is treated as ready after a short grace period.
 * Idempotent per tab-hit. The tab label is read from the URL so it matches
 * `public.hits.url` for cross-checking.
 */
export function observeLoading(isLoading: boolean): void {
  const t = tabFromLocation();
  if (t) tab = t;
  if (readyMarked) return;

  if (isLoading) {
    sawLoading = true;
    if (cachedReadyTimer) {
      clearTimeout(cachedReadyTimer);
      cachedReadyTimer = undefined;
    }
    return;
  }

  // isLoading === false
  if (sawLoading) {
    markReady();
    return;
  }

  // false without ever having seen loading: either cached (no fetch) or the
  // brief pre-fetch tick before a fetch starts. Defer; if a fetch starts the
  // isLoading=true branch cancels this and we mark on completion instead.
  armCachedReadyDefer();
}

function markReady(): void {
  if (readyMarked) return;
  readyMarked = true;
  readyAtActiveMs = activeMsNow();
  timeToReadyMs = Math.round(Date.now() - tabStartWall);
}

/**
 * End the current tab-hit and emit a snapshot. Call from AnalyticsTabRoute
 * BEFORE loadRecordHit re-keys the hit, so the event attaches the outgoing
 * tab's hit_id.
 */
export function endTab(reason: EndReason = 'route_change'): void {
  pause();
  emit(reason);
}
